import { createStore } from 'vuex';
import authStore from '@/domains/@platform/auth/auth.store';
import adminStore from '@/domains/@platform/admin/admin.store';
import chatStore from '@/domains/@bot/chat/chat.store';
import pragyaaiStore from '@/domains/@bot/pragyaai/pragyaai.store';
import kcStore from '@/domains/@content/kc/kc.store';
import itemgenStore from '@/domains/@content/item-gen/itemgen.store';
import coursegenStore from '@/domains/@content/course-content-gen/coursegen.store';
import insightsStore from '@/domains/@platform/insights/insights.store';
import ai from './modules/ai';
import { checklistStore } from '@/domains/@employer/checklist';
import { createPersistencePlugin } from './plugins/persistence';
import { clearSessionStorage } from '@/shared/storage/appStorage';

import {
  ITEM_CREATION,
  CREATE_ITEM_VALIDATION,
  ITEM_CREATION_FROM_PROMPT,
  CREATE_PROMPT_VALIDATION_ITEM,
} from '@/shared/constants/prompts/item-prompts';

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // noop: storage may be unavailable (private mode/quota/security policy)
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // noop: storage may be unavailable (private mode/quota/security policy)
  }
}

function parseLocalStorage(value) {
  if (value === 'undefined') return undefined;
  if (value === 'null') return null;
  return value;
}

function safeJsonParse(key, fallback = null) {
  try {
    const raw = safeGetItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

const moduleRegistry = {
  auth: authStore,
  admin: adminStore,
  chat: chatStore,
  pragyaai: pragyaaiStore,
  kc: kcStore,
  itemgen: itemgenStore,
  coursegen: coursegenStore,
  insights: insightsStore,
};

function authFirst(state, authKey, rootKey) {
  const hasAuthSession = !!state.auth?.token;
  const authValue = state.auth?.[authKey];
  const rootValue = state[rootKey];

  if (hasAuthSession) {
    return authValue ?? rootValue ?? '';
  }

  return rootValue ?? authValue ?? '';
}

const store = createStore({
  modules: {
    ai,
    auth: authStore,
    admin: adminStore,
    chat: chatStore,
    pragyaai: pragyaaiStore,
    kc: kcStore,
    itemgen: itemgenStore,
    coursegen: coursegenStore,
    checklist: checklistStore,
    insights: insightsStore,
  },

  plugins: [createPersistencePlugin(moduleRegistry)],

  state: {
    breadcrumbHistory: safeJsonParse('breadcrumbHistory', []),
    token: parseLocalStorage(safeGetItem('token') || ''),
    courseId: parseLocalStorage(safeGetItem('courseId') || ''),
    owningLibraryId: parseLocalStorage(safeGetItem('owningLibraryId') || ''),
    courseData: safeJsonParse('courseData', null),
    contentId: safeGetItem('contentId') || '',
    contentBody: safeGetItem('contentBody') || '',
    userId: safeGetItem('userId') || '',
    studentUserId: safeGetItem('studentUserId') || '',
    userInstitutionId: safeGetItem('userInstitutionId') || '',
    aiAdmin: false, // Server-authoritative only; never hydrate from localStorage
    userRole: safeGetItem('userRole') || '',
    userProfileId: safeGetItem('userProfileId') || '',
    assessmentId: safeGetItem('assessmentId') || '',
    courseTitle: safeGetItem('courseTitle') || '',
    courseToggle: safeGetItem('courseToggle') || 'Selected',
    kcPrompt: safeGetItem('kcPrompt') || '',
    mcPrompt: safeGetItem('mcPrompt') || '',
    cPrompt: safeGetItem('cPrompt') || '',
    kcUserPrompt: safeGetItem('kcUserPrompt') || '',
    kcCustomizationPrompt: safeGetItem('kcCustomizationPrompt') || '',
    kcValidationPrompt: safeGetItem('kcValidationPrompt') || '',
    itemCreationPrompt: safeGetItem('itemCreationPrompt') || ITEM_CREATION,
    createItemValidationPrompt: safeGetItem('createItemValidationPrompt') || CREATE_ITEM_VALIDATION,
    itemCreationFromPrompt: safeGetItem('itemCreationFromPrompt') || ITEM_CREATION_FROM_PROMPT,
    createPromptValidationItem: safeGetItem('createPromptValidationItem') || CREATE_PROMPT_VALIDATION_ITEM,
    autoContentPrompt: safeGetItem('autoContentPrompt') || '',
    extendedCoursePrompt: safeGetItem('extendedCoursePrompt') || '',
    competencyTreePrompt: safeGetItem('competencyTreePrompt') || '',
    sourceContextExamTemplate: safeGetItem('sourceContextExamTemplate') || '',
    sourceContextConceptTemplate: safeGetItem('sourceContextConceptTemplate') || '',
    topicCoverageExamTemplate: safeGetItem('topicCoverageExamTemplate') || '',
    topicCoverageConceptTemplate: safeGetItem('topicCoverageConceptTemplate') || '',
    sourceContextDescribeTemplate: safeGetItem('sourceContextDescribeTemplate') || '',
    sourceContextSyllabusTemplate: safeGetItem('sourceContextSyllabusTemplate') || '',
    sourceContextOutcomesTemplate: safeGetItem('sourceContextOutcomesTemplate') || '',
    sourceContextStandardsTemplate: safeGetItem('sourceContextStandardsTemplate') || '',
    sourceContextExamCertTemplate: safeGetItem('sourceContextExamCertTemplate') || '',
    topicCoverageDescribeTemplate: safeGetItem('topicCoverageDescribeTemplate') || '',
    topicCoverageSyllabusTemplate: safeGetItem('topicCoverageSyllabusTemplate') || '',
    topicCoverageOutcomesTemplate: safeGetItem('topicCoverageOutcomesTemplate') || '',
    topicCoverageStandardsTemplate: safeGetItem('topicCoverageStandardsTemplate') || '',
    topicCoverageExamCertTemplate: safeGetItem('topicCoverageExamCertTemplate') || '',
    combinePrecedenceInstructions: safeGetItem('combinePrecedenceInstructions') || '',
    countConstraintsTemplate: safeGetItem('countConstraintsTemplate') || '',
    pdfContentCustomizationPrompt: safeGetItem('pdfContentCustomizationPrompt') || '',
    ytContentCustomizationPrompt: safeGetItem('ytContentCustomizationPrompt') || '',
    podcastPrompt: safeGetItem('podcastPrompt') || '',
    firstName: safeGetItem('firstName') || '',
    assessmentData: safeJsonParse('assessmentData', null),
    userEnrolledCourses: safeJsonParse('userEnrolledCourses', []),
    institutionLogo: safeGetItem('institutionLogo') || '',
    institutionFolders: safeJsonParse('institutionFolders', []),
    selectedEntityName: safeGetItem('selectedEntityName') || '',
    kcActiveTab: safeGetItem('kcActiveTab') || 'KCExplanation',
    kcTabOptions: [
      { name: 'KC Explanation', value: 'KCExplanation' },
      { name: 'Answer Analysis', value: 'KCAnswerAnalysis' },
    ],
    preCannedQuestionType: safeGetItem('preCannedQuestionType') || '',
    preCannedQuestions: safeJsonParse('preCannedQuestions', null),
    preCannedRails: safeJsonParse('preCannedRails', null),
    courseDataResources: safeJsonParse('courseDataResources', null),
    courseDataKCs: safeJsonParse('courseDataKCs', null),
    aiModel: safeJsonParse('aiModel', null),
    aiFeature: safeGetItem('aiFeature') || null,
    validationModel: safeJsonParse('validationModel', null),
    careers: safeJsonParse('careers', []),
    interests: safeJsonParse('interests', []),
  },

  mutations: {
    addBreadcrumb(state, breadcrumb) {
      const fullPath = breadcrumb.query
        ? `${breadcrumb.to}?${new URLSearchParams(breadcrumb.query).toString()}`
        : breadcrumb.to;
      const newBreadcrumb = { ...breadcrumb, to: fullPath };
      const index = state.breadcrumbHistory.findIndex((b) => b.to === fullPath);
      if (index !== -1) {
        state.breadcrumbHistory = state.breadcrumbHistory.slice(0, index + 1);
      } else {
        state.breadcrumbHistory.push(newBreadcrumb);
      }
      safeSetItem('breadcrumbHistory', JSON.stringify(state.breadcrumbHistory));
    },
    clearBreadcrumbs(state) {
      state.breadcrumbHistory = [];
      safeRemoveItem('breadcrumbHistory');
    },
    setToken(state, token) {
      state.token = token;
      safeSetItem('token', token);
    },
    setCourseData(state, data) {
      state.courseData = data;
      safeSetItem('courseData', JSON.stringify(data));
    },
    setCourseId(state, id) {
      state.courseId = id;
      safeSetItem('courseId', id);
    },
    setOwningLibraryId(state, id) {
      state.owningLibraryId = id;
      safeSetItem('owningLibraryId', id);
    },
    setContentId(state, id) {
      state.contentId = id;
      safeSetItem('contentId', id);
    },
    setContentBody(state, body) {
      state.contentBody = body;
      safeSetItem('contentBody', body);
    },
    setUserId(state, id) {
      state.userId = id;
      safeSetItem('userId', id);
    },
    setStudentUserId(state, id) {
      state.studentUserId = id;
      safeSetItem('studentUserId', id);
    },
    setUserInstitutionId(state, id) {
      state.userInstitutionId = id;
      safeSetItem('userInstitutionId', id);
    },
    setAiAdmin(state, isAiAdmin) {
      state.aiAdmin = isAiAdmin;
      // Do NOT persist aiAdmin — server-authoritative only (see auth store)
    },
    setUserRole(state, role) {
      state.userRole = role;
      safeSetItem('userRole', role);
    },
    setUserProfileId(state, id) {
      state.userProfileId = id;
      safeSetItem('userProfileId', id);
    },
    setAssessmentId(state, id) {
      state.assessmentId = id;
      safeSetItem('assessmentId', id);
    },
    setCourseTitle(state, title) {
      state.courseTitle = title;
      safeSetItem('courseTitle', title);
    },
    setKcPrompt(state, prompt) {
      state.kcPrompt = prompt;
      safeSetItem('kcPrompt', prompt);
    },
    setMcPrompt(state, prompt) {
      state.mcPrompt = prompt;
      safeSetItem('mcPrompt', prompt);
    },
    setCPrompt(state, prompt) {
      state.cPrompt = prompt;
      safeSetItem('cPrompt', prompt);
    },
    setKcUserPrompt(state, prompt) {
      state.kcUserPrompt = prompt;
      safeSetItem('kcUserPrompt', prompt);
    },
    setKcCustomizationPrompt(state, prompt) {
      state.kcCustomizationPrompt = prompt;
      safeSetItem('kcCustomizationPrompt', prompt);
    },
    setKcValidationPrompt(state, prompt) {
      state.kcValidationPrompt = prompt;
      safeSetItem('kcValidationPrompt', prompt);
    },
    setItemCreationPrompt(state, prompt) {
      state.itemCreationPrompt = prompt;
      safeSetItem('itemCreationPrompt', prompt);
    },
    setCreateItemValidationPrompt(state, prompt) {
      state.createItemValidationPrompt = prompt;
      safeSetItem('createItemValidationPrompt', prompt);
    },
    setItemCreationFromPrompt(state, prompt) {
      state.itemCreationFromPrompt = prompt;
      safeSetItem('itemCreationFromPrompt', prompt);
    },
    setCreatePromptValidationItem(state, prompt) {
      state.createPromptValidationItem = prompt;
      safeSetItem('createPromptValidationItem', prompt);
    },
    setAutoContentPrompt(state, prompt) {
      state.autoContentPrompt = prompt;
      safeSetItem('autoContentPrompt', prompt);
    },
    setExtendedCoursePrompt(state, prompt) {
      state.extendedCoursePrompt = prompt;
      safeSetItem('extendedCoursePrompt', prompt);
    },
    setCompetencyTreePrompt(state, prompt) {
      state.competencyTreePrompt = prompt;
      safeSetItem('competencyTreePrompt', prompt);
    },
    setSourceContextExamTemplate(state, template) {
      state.sourceContextExamTemplate = template;
      safeSetItem('sourceContextExamTemplate', template);
    },
    setSourceContextConceptTemplate(state, template) {
      state.sourceContextConceptTemplate = template;
      safeSetItem('sourceContextConceptTemplate', template);
    },
    setTopicCoverageExamTemplate(state, template) {
      state.topicCoverageExamTemplate = template;
      safeSetItem('topicCoverageExamTemplate', template);
    },
    setTopicCoverageConceptTemplate(state, template) {
      state.topicCoverageConceptTemplate = template;
      safeSetItem('topicCoverageConceptTemplate', template);
    },
    setSourceContextDescribeTemplate(state, template) {
      state.sourceContextDescribeTemplate = template;
      safeSetItem('sourceContextDescribeTemplate', template);
    },
    setSourceContextSyllabusTemplate(state, template) {
      state.sourceContextSyllabusTemplate = template;
      safeSetItem('sourceContextSyllabusTemplate', template);
    },
    setSourceContextOutcomesTemplate(state, template) {
      state.sourceContextOutcomesTemplate = template;
      safeSetItem('sourceContextOutcomesTemplate', template);
    },
    setSourceContextStandardsTemplate(state, template) {
      state.sourceContextStandardsTemplate = template;
      safeSetItem('sourceContextStandardsTemplate', template);
    },
    setSourceContextExamCertTemplate(state, template) {
      state.sourceContextExamCertTemplate = template;
      safeSetItem('sourceContextExamCertTemplate', template);
    },
    setTopicCoverageDescribeTemplate(state, template) {
      state.topicCoverageDescribeTemplate = template;
      safeSetItem('topicCoverageDescribeTemplate', template);
    },
    setTopicCoverageSyllabusTemplate(state, template) {
      state.topicCoverageSyllabusTemplate = template;
      safeSetItem('topicCoverageSyllabusTemplate', template);
    },
    setTopicCoverageOutcomesTemplate(state, template) {
      state.topicCoverageOutcomesTemplate = template;
      safeSetItem('topicCoverageOutcomesTemplate', template);
    },
    setTopicCoverageStandardsTemplate(state, template) {
      state.topicCoverageStandardsTemplate = template;
      safeSetItem('topicCoverageStandardsTemplate', template);
    },
    setTopicCoverageExamCertTemplate(state, template) {
      state.topicCoverageExamCertTemplate = template;
      safeSetItem('topicCoverageExamCertTemplate', template);
    },
    setCombinePrecedenceInstructions(state, template) {
      state.combinePrecedenceInstructions = template;
      safeSetItem('combinePrecedenceInstructions', template);
    },
    setCountConstraintsTemplate(state, template) {
      state.countConstraintsTemplate = template;
      safeSetItem('countConstraintsTemplate', template);
    },
    setPdfContentCustomizationPrompt(state, prompt) {
      state.pdfContentCustomizationPrompt = prompt;
      safeSetItem('pdfContentCustomizationPrompt', prompt);
    },
    setYtContentCustomizationPrompt(state, prompt) {
      state.ytContentCustomizationPrompt = prompt;
      safeSetItem('ytContentCustomizationPrompt', prompt);
    },
    setPodcastPrompt(state, prompt) {
      state.podcastPrompt = prompt;
      safeSetItem('podcastPrompt', prompt);
    },
    setFirstName(state, firstName) {
      state.firstName = firstName;
      safeSetItem('firstName', firstName);
    },
    setAssessmentData(state, data) {
      state.assessmentData = data;
      safeSetItem('assessmentData', JSON.stringify(data));
    },
    toggleCourseState(state) {
      state.courseToggle = state.courseToggle === 'All' ? 'Selected' : 'All';
      safeSetItem('courseToggle', state.courseToggle);
    },
    setUserEnrolledCourses(state, courses) {
      state.userEnrolledCourses = courses;
      safeSetItem('userEnrolledCourses', JSON.stringify(courses));
    },
    setInstitutionLogo(state, logo) {
      state.institutionLogo = logo;
      safeSetItem('institutionLogo', logo);
    },
    setInstitutionFolders(state, folders) {
      state.institutionFolders = folders;
      safeSetItem('institutionFolders', JSON.stringify(folders));
    },
    setSelectedEntityName(state, name) {
      state.selectedEntityName = name;
      safeSetItem('selectedEntityName', name);
    },
    setKcActiveTab(state, tab) {
      state.kcActiveTab = tab;
      safeSetItem('kcActiveTab', tab);
    },
    setPreCannedQuestionType(state, type) {
      state.preCannedQuestionType = type;
      safeSetItem('preCannedQuestionType', type);
    },
    setPreCannedQuestions(state, questions) {
      state.preCannedQuestions = questions;
      safeSetItem('preCannedQuestions', JSON.stringify(questions));
    },
    setPreCannedRails(state, rails) {
      state.preCannedRails = rails;
      safeSetItem('preCannedRails', JSON.stringify(rails));
    },
    setCourseDataResources(state, { courseId, courseData }) {
      state.courseDataResources = { courseId, courseData };
      safeSetItem('courseDataResources', JSON.stringify(state.courseDataResources));
    },
    setCourseDataKCs(state, payload) {
      if (payload === null) {
        state.courseDataKCs = null;
        safeRemoveItem('courseDataKCs');
      } else {
        const { courseId, courseData } = payload;
        state.courseDataKCs = { courseId, courseData };
        safeSetItem('courseDataKCs', JSON.stringify(state.courseDataKCs));
      }
    },
    addUserEnrolledCourse(state, course) {
      state.userEnrolledCourses.push(course);
      safeSetItem('userEnrolledCourses', JSON.stringify(state.userEnrolledCourses));
    },
    setAiModel(state, model) {
      state.aiModel = model;
      safeSetItem('aiModel', JSON.stringify(model));
    },
    setAiFeature(state, features) {
      const arr = Array.isArray(features) ? features : [features];
      state.aiFeature = arr;
      safeSetItem('aiFeature', JSON.stringify(arr));
    },
    setValidationModel(state, model) {
      state.validationModel = model;
      safeSetItem('validationModel', JSON.stringify(model));
    },
    setCareers(state, careers) {
      state.careers = careers;
      safeSetItem('careers', JSON.stringify(careers));
    },
    setInterests(state, interests) {
      state.interests = interests;
      safeSetItem('interests', JSON.stringify(interests));
    },
    resetStore(state) {
      state.breadcrumbHistory = [];
      state.token = '';
      state.courseId = '';
      state.owningLibraryId = '';
      state.courseData = null;
      state.contentId = '';
      state.contentBody = '';
      state.userId = '';
      state.studentUserId = '';
      state.userInstitutionId = '';
      state.aiAdmin = false;
      state.userRole = '';
      state.userProfileId = '';
      state.assessmentId = '';
      state.courseTitle = '';
      state.courseToggle = 'Selected';
      state.kcPrompt = '';
      state.mcPrompt = '';
      state.cPrompt = '';
      state.kcUserPrompt = '';
      state.kcCustomizationPrompt = '';
      state.kcValidationPrompt = '';
      state.itemCreationPrompt = '';
      state.createItemValidationPrompt = '';
      state.itemCreationFromPrompt = '';
      state.createPromptValidationItem = '';
      state.autoContentPrompt = '';
      state.extendedCoursePrompt = '';
      state.competencyTreePrompt = '';
      state.sourceContextExamTemplate = '';
      state.sourceContextConceptTemplate = '';
      state.topicCoverageExamTemplate = '';
      state.topicCoverageConceptTemplate = '';
      state.sourceContextDescribeTemplate = '';
      state.sourceContextSyllabusTemplate = '';
      state.sourceContextOutcomesTemplate = '';
      state.sourceContextStandardsTemplate = '';
      state.sourceContextExamCertTemplate = '';
      state.topicCoverageDescribeTemplate = '';
      state.topicCoverageSyllabusTemplate = '';
      state.topicCoverageOutcomesTemplate = '';
      state.topicCoverageStandardsTemplate = '';
      state.topicCoverageExamCertTemplate = '';
      state.combinePrecedenceInstructions = '';
      state.countConstraintsTemplate = '';
      state.pdfContentCustomizationPrompt = '';
      state.ytContentCustomizationPrompt = '';
      state.podcastPrompt = '';
      state.firstName = '';
      state.assessmentData = null;
      state.userEnrolledCourses = [];
      state.institutionLogo = '';
      state.institutionFolders = [];
      state.selectedEntityName = '';
      state.kcActiveTab = 'KCExplanation';
      state.preCannedQuestionType = '';
      state.preCannedQuestions = null;
      state.preCannedRails = null;
      state.courseDataResources = null;
      state.courseDataKCs = null;
      state.aiModel = null;
      state.aiFeature = null;
      state.validationModel = null;
      state.careers = [];
      state.interests = [];

      // Single source of truth for storage cleanup.
      clearSessionStorage();
    },
  },

  getters: {
    getUserRole: (state) => authFirst(state, 'userRole', 'userRole'),
    getUserId: (state) => authFirst(state, 'userId', 'userId'),
    getFirstName: (state) => authFirst(state, 'firstName', 'firstName'),
    getUserProfileId: (state) => authFirst(state, 'userProfileId', 'userProfileId'),
    getUserInstitutionId: (state) => authFirst(state, 'institutionId', 'userInstitutionId'),
    getInstitutionLogo: (state) => authFirst(state, 'institutionLogo', 'institutionLogo'),
    isStudent: (state) => {
      const catalog = state.auth?.roleCatalog;
      const wire = catalog?.keyMap?.STUDENT;
      const role = state.auth?.userRole || state.userRole;
      return wire ? role === wire : false;
    },
    getAiFeature: (state) => state.aiFeature || state.auth?.enabledFeatures,
    getCourseId: (state) => state.courseId || state.kc?.courseId,
    getOwningLibraryId: (state) => state.owningLibraryId,
    getCourseData: (state) => state.courseData,
    getContentId: (state) => state.contentId,
    getContentBody: (state) => state.contentBody,
    getStudentUserId: (state) => state.studentUserId,
    getSelectedEntityName: (state) => state.selectedEntityName,
    getAssessmentId: (state) => state.assessmentId,
    getCourseTitle: (state) => state.courseTitle,
    getKCPrompt: (state) => state.kcPrompt,
    getMCPrompt: (state) => state.mcPrompt,
    getCPrompt: (state) => state.cPrompt,
    getKCUserPrompt: (state) => state.kcUserPrompt,
    getKCCustomizationPrompt: (state) => state.kcCustomizationPrompt,
    getKCValidationPrompt: (state) => state.kcValidationPrompt,
    getItemCreationPrompt: (state) => state.itemCreationPrompt,
    getCreateItemValidationPrompt: (state) => state.createItemValidationPrompt,
    getItemCreationFromPrompt: (state) => state.itemCreationFromPrompt,
    getCreatePromptValidationItem: (state) => state.createPromptValidationItem,
    getAutoContentPrompt: (state) => state.autoContentPrompt,
    getExtendedCoursePrompt: (state) => state.extendedCoursePrompt,
    getCompetencyTreePrompt: (state) => state.competencyTreePrompt,
    getPdfContentCustomizationPrompt: (state) => state.pdfContentCustomizationPrompt,
    getYtContentCustomizationPrompt: (state) => state.ytContentCustomizationPrompt,
    getPodcastPrompt: (state) => state.podcastPrompt,
    getAssessmentData: (state) => state.assessmentData,
    getCourseToggle: (state) => state.courseToggle,
    getUserEnrolledCourses: (state) => state.userEnrolledCourses,
    getKCActiveTab: (state) => state.kcActiveTab,
    getKCTabOptions: (state) => state.kcTabOptions,
    getPreCannedQuestionType: (state) => state.preCannedQuestionType,
    getPreCannedQuestions: (state) => state.preCannedQuestions,
    getPreCannedRails: (state) => state.preCannedRails,
    getCourseDataResources: (state) => state.courseDataResources,
    getCourseDataKCs: (state) => state.courseDataKCs,
    getAiModel: (state) => state.aiModel,
    getValidationModel: (state) => state.validationModel,
    getCareers: (state) => state.careers,
    getInterests: (state) => state.interests,
  },

  actions: {
    resetAll({ commit, dispatch }) {
      dispatch('auth/logout');
      commit('admin/RESET');
      commit('chat/RESET');
      commit('pragyaai/RESET');
      commit('kc/RESET');
      commit('itemgen/RESET');
      commit('coursegen/RESET');
      commit('ai/RESET');
    },
  },
});

export default store;
